import { Compass, Layers, Sparkles, Sun } from "lucide-react";
import type { EnergyHistory, Reading } from "@spf/contracts";
import { Card } from "../components/Card.js";
import { Freshness } from "../components/Freshness.js";
import { CrossCheck } from "../components/analytics/CrossCheck.js";
import { Stat } from "../components/widgets/Stat.js";
import { Empty, Failed, Loading } from "../components/ViewState.js";
import { useLiveTelemetry } from "../lib/useLiveTelemetry.js";
import { useFreshness } from "../lib/useFreshness.js";
import { useEnergy } from "../lib/useEnergy.js";
import {
  LEDGER_QUERIES,
  TODAY_QUERY,
  coverageNote,
  formatKwh,
  recordedSince,
} from "../lib/energy.js";
import { formatUnit } from "../lib/format.js";
import type { Async } from "../lib/useAsync.js";

/**
 * What the panels are generating, what they have generated, and whether this
 * system's arithmetic agrees with the inverter's own count of it.
 *
 * Until slice 5c this page carried no figure at all, by rule: the solar fields
 * had never been seen non-zero, so a zero could not be told from a broken
 * decoder (`DECISIONS/0023`). An array was connected on 2026-09-27 and the
 * fields were verified against ShinePhone, the power balance and the
 * inverter's counters (`DECISIONS/0028`), so the figures are now shown -- each
 * beside the inverter's own, and the lifetime pair held to a check that raises
 * an alarm when they part.
 *
 * The design's roof schematic of eighteen panels is still not drawn. The
 * inverter measures the string as one, so a per-panel wattage or cell-health
 * dot would be invented; the page says so rather than decorating the gap.
 */
export function SolarPage() {
  const { latest } = useLiveTelemetry();
  const freshness = useFreshness(latest);
  const reading = latest?.reading ?? null;

  return (
    <section aria-labelledby="solar-heading" className="flex flex-col gap-5">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
            <Layers size={14} aria-hidden="true" className="text-accent-2-ink" />
            Generation from the panels
          </p>
          <h2 id="solar-heading" className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Solar
          </h2>
        </div>

        {reading !== null && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl border border-line bg-card p-2">
            <Pill label="Output now" value={formatUnit(reading.pvPowerW, "W", true)} />
            <span aria-hidden="true" className="h-8 w-px bg-line" />
            <Pill label="String voltage" value={formatUnit(reading.pvVoltageV, "V")} />
          </div>
        )}
      </div>

      {latest !== null && reading !== null && <Freshness value={latest} freshness={freshness} />}

      {latest === null ? (
        <Card title="Generation">
          <Loading label="Connecting for live updates…" />
        </Card>
      ) : reading === null ? (
        <Card title="Generation">
          <Empty>
            No telemetry yet. What the panels generate appears here once the inverter reports.
          </Empty>
        </Card>
      ) : (
        <Generation reading={reading} />
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Orientation
          icon={<Sparkles size={22} aria-hidden="true" className="text-accent-2-ink" />}
          label="Panels"
          value="Measured together, as one string"
        />
        <Orientation
          icon={<Compass size={22} aria-hidden="true" className="text-accent-ink" />}
          label="Azimuth and tilt"
          value="Not surveyed"
        />
        <Orientation
          icon={<Sun size={22} aria-hidden="true" className="text-accent-ink" />}
          label="Second input"
          value="Nothing connected"
        />
      </div>
    </section>
  );
}

/**
 * Today and since records began, each worked out here from the panels' power
 * and set beside the inverter's own counter. The two are not expected to match
 * to the decimal: the counter keeps counting through gaps in the record, and
 * the lifetime counter started when the inverter did.
 */
function Generation({ reading }: { reading: Reading }) {
  const today = useEnergy(TODAY_QUERY, reading.time);
  const lifetime = useEnergy(LEDGER_QUERIES.lifetime, reading.time);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="Generated today">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Derived state={today.state} onRetry={today.retry} note={todayNote} />
          <Stat
            label="By the inverter's counter"
            value={formatUnit(reading.pvEnergyTodayKwh, "kWh")}
          />
        </div>
      </Card>

      <Card title="Generated since records began">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Derived state={lifetime.state} onRetry={lifetime.retry} note={recordedSince} />
          <Stat
            label="By the inverter's counter, since installation"
            value={formatUnit(reading.pvEnergyTotalKwh, "kWh")}
          />
        </div>
        <div className="mt-6 border-t border-line pt-4">
          <CrossCheck lifetime={lifetime.state} kinds={["solar"]} onRetry={lifetime.retry} />
        </div>
      </Card>
    </div>
  );
}

function todayNote(): string {
  return "Worked out from today's readings.";
}

function Derived({
  state,
  onRetry,
  note,
}: {
  state: Async<EnergyHistory>;
  onRetry: () => void;
  note: (history: EnergyHistory) => string;
}) {
  if (state.kind === "loading") return <Loading label="Loading…" />;
  if (state.kind === "error") return <Failed message={state.message} onRetry={onRetry} />;
  const { totals } = state.value;
  const caveat = coverageNote(totals);
  const words = note(state.value);
  return (
    <Stat
      label="Worked out here"
      value={formatKwh(totals.solarKwh)}
      note={caveat === null || totals.solarKwh === null ? words : `${words} ${caveat}.`}
      tone="accent"
    />
  );
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <p className="px-3 py-1 text-center">
      <span className="block text-[11px] text-ink-muted">{label}</span>
      <span className="tabular block text-base font-bold text-ink">{value}</span>
    </p>
  );
}

function Orientation({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <section className="flex items-center gap-3 rounded-card border border-line bg-card p-4">
      {icon}
      <div>
        <h3 className="block text-xs text-ink-muted">{label}</h3>
        <p className="text-sm font-bold text-ink">{value}</p>
      </div>
    </section>
  );
}
